import { px } from "@mantine/core"
import { theme } from "@tfg/themes"

import type {
  OperationGraphNodeDto,
  OperationNodeDto,
} from "~/gen/api/types"
import {
  FORK_ROOT_SUPPLIER_CARD_HEIGHT,
  SUPPLIER_CARD_HEIGHT,
} from "~/pages/Brand/Products/Details/constants.ts"

import {
  getRenderedChildren,
  isOperationGraphNode,
  isSourceContextGraphNode,
  type BrandTraceabilityGraphNode,
  type RenderedChild,
} from "./traceability-graph.view-model"

const STACK_ROW_GAP = Number(px(theme.spacing.md))

type GetNodesByIdArgs = {
  readonly isMultisourceEnabled: boolean
  readonly operationNodes: readonly OperationNodeDto[] | undefined
  readonly graphNodes: readonly BrandTraceabilityGraphNode[] | undefined
}

type GetMainItemArgs = {
  readonly isMultisourceEnabled: boolean
  readonly nodesById: Map<string, BrandTraceabilityGraphNode>
  readonly graphRootId: string | undefined
}

export const getNodesById = ({
  isMultisourceEnabled,
  operationNodes,
  graphNodes,
}: GetNodesByIdArgs): Map<string, BrandTraceabilityGraphNode> => {
  const nodes = isMultisourceEnabled
    ? graphNodes ?? []
    : (operationNodes ?? []).map(normalizeLegacyOperationNode)

  return new Map(nodes.map((node) => [node.id, node]))
}

export const getMainItem = ({
  isMultisourceEnabled,
  nodesById,
  graphRootId,
}: GetMainItemArgs): BrandTraceabilityGraphNode | undefined => {
  if (isMultisourceEnabled && graphRootId !== undefined) {
    return nodesById.get(graphRootId)
  }

  for (const node of nodesById.values()) {
    if (isOperationGraphNode(node) && node.isMain) {
      return node
    }
  }

  return undefined
}

export const getVerticalDividerCoveredHeight = (
  children: RenderedChild[],
  nodesById: Map<string, BrandTraceabilityGraphNode>
): number => {
  if (children.length <= 1) {
    return 0
  }

  const coveredHeightBeforeLastSibling = children
    .map(({ node }) => getRenderedBranchHeight(node, nodesById))
    .slice(0, -1)
    .reduce((sum, height) => sum + height, 0)

  return coveredHeightBeforeLastSibling + STACK_ROW_GAP * (children.length - 1)
}

export const shouldRenderChildDivider = (
  parentNode: BrandTraceabilityGraphNode,
  childNode: BrandTraceabilityGraphNode,
  childIndex: number
): boolean => {
  if (isSourceContextGraphNode(childNode)) {
    return true
  }

  return childIndex > 0 || !childNode.id.includes(parentNode.type)
}

const getRenderedBranchHeight = (
  item: BrandTraceabilityGraphNode,
  nodesById: Map<string, BrandTraceabilityGraphNode>
): number => {
  const ownCardHeight = getNodeCardHeight(item)
  const renderedChildren = getRenderedChildren(item, nodesById)

  if (renderedChildren.length === 0) {
    return ownCardHeight
  }

  const childrenHeight = renderedChildren.reduce(
    (sum, { node: childNode }) => sum + getRenderedBranchHeight(childNode, nodesById),
    0
  )
  const rowGapsHeight = STACK_ROW_GAP * (renderedChildren.length - 1)

  return Math.max(ownCardHeight, childrenHeight + rowGapsHeight)
}

const getNodeCardHeight = (node: BrandTraceabilityGraphNode): number => {
  return isSourceContextGraphNode(node)
    ? FORK_ROOT_SUPPLIER_CARD_HEIGHT
    : SUPPLIER_CARD_HEIGHT
}

const normalizeLegacyOperationNode = (
  node: OperationNodeDto
): OperationGraphNodeDto => {
  return {
    ...node,
    nodeType: "operation",
  }
}
